import type { CSSProperties, SVGProps } from 'react';
import type { UniverseAssetType } from '../lib/types';

type GlyphProps = SVGProps<SVGSVGElement>;

/** Cut-outs are drawn in the tile's own tint so the glyph reads as a stencil. */
const CUT = 'var(--glyph-cut, #fff)';

const DOC_BODY =
  'M6 2.25h5.4c.4 0 .78.16 1.06.44l3.35 3.35c.28.28.44.66.44 1.06v9.4A1.75 1.75 0 0 1 14.5 18.25h-8.5A1.75 1.75 0 0 1 4.25 16.5V4A1.75 1.75 0 0 1 6 2.25Z';
const DOC_FOLD = 'M11.25 2.6v3a1.4 1.4 0 0 0 1.4 1.4h3';

function PdfGlyph(p: GlyphProps) {
  return (
    <svg viewBox="0 0 20 20" aria-hidden {...p}>
      <path d={DOC_BODY} fill="currentColor" />
      <path d={DOC_FOLD} fill="none" stroke={CUT} strokeWidth="1.2" strokeLinejoin="round" />
      <rect x="6.6" y="9" width="3.6" height="3.6" rx="0.8" fill={CUT} />
      <path d="M11.6 9.8h2.2M11.6 11.8h2.2M6.6 14.8h7.2" stroke={CUT} strokeWidth="1.3" strokeLinecap="round" />
    </svg>
  );
}

function DocumentGlyph(p: GlyphProps) {
  return (
    <svg viewBox="0 0 20 20" aria-hidden {...p}>
      <path d={DOC_BODY} fill="currentColor" />
      <path d={DOC_FOLD} fill="none" stroke={CUT} strokeWidth="1.2" strokeLinejoin="round" />
      <path d="M6.9 10h6.6M6.9 12.5h6.6M6.9 15h4.2" stroke={CUT} strokeWidth="1.3" strokeLinecap="round" />
    </svg>
  );
}

function VideoGlyph(p: GlyphProps) {
  return (
    <svg viewBox="0 0 20 20" aria-hidden {...p}>
      <path
        d="M6 4.1v11.8a1.05 1.05 0 0 0 1.6.9l9.4-5.9a1.05 1.05 0 0 0 0-1.8L7.6 3.2A1.05 1.05 0 0 0 6 4.1Z"
        fill="currentColor"
      />
    </svg>
  );
}

function AudioGlyph(p: GlyphProps) {
  return (
    <svg viewBox="0 0 20 20" aria-hidden {...p}>
      <path
        d="M16.9 2.55a.9.9 0 0 0-1.1-.88L8.1 3.4a.9.9 0 0 0-.7.88v8.2a3 3 0 1 0 1.8 2.72V7.72l5.9-1.33v4.3a3 3 0 1 0 1.8 2.72Z"
        fill="currentColor"
      />
    </svg>
  );
}

function PresentationGlyph(p: GlyphProps) {
  return (
    <svg viewBox="0 0 20 20" aria-hidden {...p}>
      <rect x="2.25" y="3" width="15.5" height="10.75" rx="2" fill="currentColor" />
      <path d="M7 11V9.2M10 11V6.6M13 11V8" stroke={CUT} strokeWidth="1.5" strokeLinecap="round" />
      <path d="M10 13.75v2.6M6.75 17.25h6.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  );
}

function SpreadsheetGlyph(p: GlyphProps) {
  return (
    <svg viewBox="0 0 20 20" aria-hidden {...p}>
      <rect x="2.75" y="3.25" width="14.5" height="13.5" rx="2.25" fill="currentColor" />
      <path d="M2.75 7.9h14.5M2.75 12.3h14.5M7.9 3.25v13.5" stroke={CUT} strokeWidth="1.2" />
    </svg>
  );
}

function WebsiteGlyph(p: GlyphProps) {
  return (
    <svg
      viewBox="0 0 20 20"
      aria-hidden
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...p}
    >
      <circle cx="10" cy="10" r="7.4" />
      <ellipse cx="10" cy="10" rx="3.2" ry="7.4" />
      <path d="M2.8 10h14.4M4 6.1h12M4 13.9h12" />
    </svg>
  );
}

const GLYPHS: Record<UniverseAssetType, (p: GlyphProps) => JSX.Element> = {
  pdf: PdfGlyph,
  video: VideoGlyph,
  audio: AudioGlyph,
  presentation: PresentationGlyph,
  document: DocumentGlyph,
  spreadsheet: SpreadsheetGlyph,
  link: WebsiteGlyph,
};

export function TypeGlyph({ type, ...props }: GlyphProps & { type: UniverseAssetType }) {
  const Glyph = GLYPHS[type] ?? DocumentGlyph;
  return <Glyph {...props} />;
}

const TILE_SIZES = { xs: 16, sm: 22, md: 28, lg: 56 } as const;

interface TileProps {
  type: UniverseAssetType;
  size?: keyof typeof TILE_SIZES;
  className?: string;
  style?: CSSProperties;
}

/** iOS app-icon style tile: continuous-corner squircle, vertical tint gradient, white stencil glyph. */
export function TypeTile({ type, size = 'sm', className = '', style }: TileProps) {
  const px = TILE_SIZES[size];
  return (
    <span
      aria-hidden="true"
      className={`type-tile ${className}`}
      data-type={type}
      style={{ width: px, height: px, ...style }}
    >
      <TypeGlyph type={type} style={{ width: '64%', height: '64%' }} />
    </span>
  );
}
